/**
 * Keylines — the draughtsman's marks Vesper uses to separate zones on a data
 * screen.
 *
 *   KeylineRule  a labelled section rule: "YOUR AIRCRAFT · 3 sub-fleets",
 *                a ruled line with a faint 24px scale and a tick at each end,
 *                and a note at the far end ("3 of 10 sub-fleets")
 *   KeylineTick  a rule with end ticks: short, the divider between groups on
 *                the rail; full, across its container, under a card's title
 *   CropMarks    the four corner marks on a card's artwork
 *
 * They separate zones; they never sit on the globe, inside a table or on the
 * year-end board. All three are decoration around content that already says
 * what it is, so they carry no role of their own.
 */

import type { ReactNode } from 'react';

import styles from './keyline.module.css';

interface KeylineRuleProps {
  /** The section's name, in the brighter label ink. */
  lead: ReactNode;
  /** What the section holds, after a middle dot. */
  detail?: ReactNode;
  /** A note at the far end of the rule. */
  aside?: ReactNode;
  /**
   * Make the name a heading at this level, so the section the rule opens is in
   * the page's outline. It looks the same either way.
   */
  level?: 2 | 3 | 4 | 5 | 6;
  className?: string;
}

export function KeylineRule({ lead, detail, aside, level, className = '' }: KeylineRuleProps) {
  const Name = level ? (`h${level}` as const) : 'span';
  return (
    <div className={[styles.rule, className].filter(Boolean).join(' ')}>
      <Name className={styles.name}>
        <b className={styles.lead}>{lead}</b>
        {detail != null && <> · {detail}</>}
      </Name>
      <i className={styles.line} aria-hidden="true" />
      {aside != null && <span>{aside}</span>}
    </div>
  );
}

const LENGTH = {
  short: styles.short,
  full: styles.full,
} as const;

export function KeylineTick({ length = 'short', className = '' }: { length?: keyof typeof LENGTH; className?: string }) {
  return <span className={[styles.tick, LENGTH[length], className].filter(Boolean).join(' ')} aria-hidden="true" />;
}

/** Corner marks inside a positioned parent, 8px in from its edges. */
export function CropMarks({ className = '' }: { className?: string }) {
  return (
    <span className={[styles.crop, className].filter(Boolean).join(' ')} aria-hidden="true">
      <i />
      <i />
      <i />
      <i />
    </span>
  );
}
